import { api } from '@packages/backend/convex/_generated/api';
import { getLinklyDeviceLinklyEnv, getUserErrorMessage, type LinklyEnv } from '@packages/shared';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import { useQuery } from 'convex-helpers/react';
import { formatDistanceToNow } from 'date-fns';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { ActionButton } from '@/components/ActionButton';
import { ActionDropdown } from '@/components/ActionDropdown';
import { DropdownMenuItemDestructive } from '@/components/DropdownMenuItemDestructive';
import { ErrorAlert } from '@/components/ErrorAlert';
import { LoadingIcon } from '@/components/LoadingIcon';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TimeDisplay } from '@/routes/admin/linkly/TimeDisplay';
import { LinklyDeviceConnectionStatus } from './LinklyDeviceConnectionStatus';
import { LinklyDeviceSessionsCard } from './LinklyDeviceSessionsCard';
import { LinklyEnvToggle } from './LinklyEnvToggle';
import { PairLinklyDeviceDialog } from './PairLinklyDeviceDialog';

export const Route = createFileRoute('/a/linkly/devices/$linklyDeviceId')({
  staticData: {
    breadcrumbs: [{ label: 'Linkly Devices', url: '/a/linkly/devices' }, { label: 'Details' }],
  },
  component: LinklyDeviceDetailsPage,
});

const ENV_HELPER_TEXT: Record<LinklyEnv, string> = {
  emulator: 'Routes to the OrderHub Linkly emulator. No real device or card.',
  sandbox: 'Routes to Linkly sandbox. Pairs Linkly Windows VPP. No real cards.',
  production: 'Routes to Linkly production. Pairs a physical EFTPOS terminal. Real cards.',
};

function LinklyDeviceDetailsPage() {
  const { linklyDeviceId } = Route.useParams();
  const { data, error, isError, isPending } = useQuery(api.linklyDevices.listAll);
  const [pairDialogOpen, setPairDialogOpen] = useState(false);
  const convex = useConvex();

  const handleRefreshToken = async () => {
    try {
      await convex.mutation(api.linklyDevices.refreshToken, { linklyDeviceId });
      toast.success('Token refresh initiated');
    } catch (error) {
      toast.error('Failed to refresh token', { description: getUserErrorMessage(error) });
    }
  };

  const handleGetPinPadStatus = async () => {
    try {
      const result = await convex.action(api.linklyDevices.getStatus, { linklyDeviceId });
      toast.success('Pin pad status', {
        description: result?.response ? JSON.stringify(result.response, null, 2) : 'No result',
      });
    } catch (error) {
      toast.error('Error', { description: getUserErrorMessage(error) });
    }
  };

  const handleClearCredentials = async () => {
    try {
      await convex.mutation(api.linklyDevices.clearCredentials, { linklyDeviceId });
      toast.success('Credentials cleared successfully');
    } catch (error) {
      toast.error('Failed to clear credentials', { description: getUserErrorMessage(error) });
    }
  };

  if (isPending) return <LoadingIcon />;
  if (isError) {
    return <ErrorAlert message={error.message} title="Error loading Linkly device details" />;
  }

  const selectedDeviceData = data?.find((d) => d.linklyDevice._id === linklyDeviceId);
  if (!selectedDeviceData) return <div>Linkly device not found.</div>;

  const { linklyDevice: device, restaurantName, connectionStatus } = selectedDeviceData;
  const linklyEnv = getLinklyDeviceLinklyEnv(device);

  return (
    <div>
      <div className="mb-4">
        <ActionButton asChild variant="outline" size="sm">
          <Link to="/a/linkly/devices">
            <ArrowLeft className="h-4 w-4" />
            Back to list
          </Link>
        </ActionButton>
      </div>

      {device.offlineAt && (
        <Alert variant="destructive" className="mb-4">
          <AlertTitle>
            OFFLINE. Last seen approx{' '}
            {formatDistanceToNow(new Date(device.offlineAt), { addSuffix: true })}.
          </AlertTitle>
          <AlertDescription className="flex items-center justify-between">
            <span>
              Connection has been lost to the EFTPOS device. It may have been:
              <br /> - Unpaired by the user
              <br /> - Lost internet
              <br /> - Powered off
            </span>
            <ActionButton size="sm" variant="outline" onClick={() => setPairDialogOpen(true)}>
              Pair again
            </ActionButton>
          </AlertDescription>
        </Alert>
      )}

      <Card className="mb-6">
        <CardHeader>
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-3">
              <CardTitle>Linkly Device</CardTitle>
              <LinklyEnvToggle linklyDeviceId={linklyDeviceId} linklyEnv={linklyEnv} />
            </div>
            <p className="text-sm text-muted-foreground">{ENV_HELPER_TEXT[linklyEnv]}</p>
          </div>
          <CardAction>
            <ActionDropdown>
              <DropdownMenuItem onSelect={handleRefreshToken}>Refresh Token</DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setPairDialogOpen(true)}>
                Pair again
              </DropdownMenuItem>

              <DropdownMenuItemDestructive onSelect={handleClearCredentials}>
                Clear credentials
              </DropdownMenuItemDestructive>
            </ActionDropdown>
          </CardAction>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-1/3">Property</TableHead>
                <TableHead>Value</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell className="font-medium">Restaurant</TableCell>
                <TableCell>{restaurantName ?? '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">Username</TableCell>
                <TableCell>
                  {device.username} / {device.password}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">POS Terminal UUID</TableCell>
                <TableCell>{device.posTerminalUuid ?? '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">Status</TableCell>
                <TableCell>
                  <LinklyDeviceConnectionStatus connectionStatus={connectionStatus} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">Token expires</TableCell>
                <TableCell>
                  <TimeDisplay timestamp={device.tokenExpiresAt} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">Last Updated</TableCell>
                <TableCell>
                  <TimeDisplay timestamp={device.updatedAt} showTime={false} />
                </TableCell>
              </TableRow>
              {device.secretGeneratedAt && (
                <TableRow>
                  <TableCell className="font-medium">Paired</TableCell>
                  <TableCell>
                    <TimeDisplay timestamp={device.secretGeneratedAt} showTime={false} />
                  </TableCell>
                </TableRow>
              )}
              {device.tokenGenerationError && (
                <TableRow>
                  <TableCell className="font-medium">Token Generation Error</TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="text-red-600">{device.tokenGenerationError}</div>
                      {device.tokenGenerationErrorAt && (
                        <div className="text-sm text-muted-foreground">
                          <TimeDisplay timestamp={device.tokenGenerationErrorAt} showTime={true} />
                        </div>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {device.secretGenerationError && (
                <TableRow>
                  <TableCell className="font-medium">Secret Generation Error</TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="text-red-600">{device.secretGenerationError}</div>
                      {device.secretGenerationErrorAt && (
                        <div className="text-sm text-muted-foreground">
                          <TimeDisplay timestamp={device.secretGenerationErrorAt} showTime={true} />
                        </div>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Status</CardTitle>
          <CardAction>
            <ActionButton variant="outline" size="sm" onClick={handleGetPinPadStatus}>
              Get status
            </ActionButton>
          </CardAction>
        </CardHeader>
        <CardContent>
          {device.status && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-1/3">Property</TableHead>
                  <TableHead>Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {device.status.bankDescription && (
                  <TableRow>
                    <TableCell className="font-medium">Bank Description</TableCell>
                    <TableCell>{device.status.bankDescription}</TableCell>
                  </TableRow>
                )}
                {device.status.timeout !== null && (
                  <TableRow>
                    <TableCell className="font-medium">Timeout</TableCell>
                    <TableCell>{device.status.timeout}ms</TableCell>
                  </TableRow>
                )}
                {device.status.pinPadSerialNumber && (
                  <TableRow>
                    <TableCell className="font-medium">Pin Pad Serial Number</TableCell>
                    <TableCell>{device.status.pinPadSerialNumber}</TableCell>
                  </TableRow>
                )}
                {device.status.pinPadVersion && (
                  <TableRow>
                    <TableCell className="font-medium">Pin Pad Version</TableCell>
                    <TableCell>{device.status.pinPadVersion}</TableCell>
                  </TableRow>
                )}
                {device.status.eftTerminalType && (
                  <TableRow>
                    <TableCell className="font-medium">EFT Terminal Type</TableCell>
                    <TableCell>{device.status.eftTerminalType}</TableCell>
                  </TableRow>
                )}
                <TableRow>
                  <TableCell className="font-medium">Status Last Updated</TableCell>
                  <TableCell>
                    <TimeDisplay timestamp={device.status.updatedAt} showTime={true} />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <LinklyDeviceSessionsCard linklyDeviceId={linklyDeviceId} />

      {pairDialogOpen && (
        <PairLinklyDeviceDialog
          linklyDeviceId={linklyDeviceId}
          onClose={() => setPairDialogOpen(false)}
        />
      )}
    </div>
  );
}
